import React, { useState, useEffect } from 'react';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';
import { useSession } from './SessionContext';
import { usePopupClose } from './usePopupClose';

const RawJsonEditor = ({ 
  isOpen, 
  onClose, 
  data, 
  title = "Raw JSON Editor",
  description = "Edit the JSON data directly.",
  rootName = "data",
  hiddenAttributes = [],
  onSave,
  saveButtonText = "Save"
}) => {
  const { user } = useSession();
  const [editedData, setEditedData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Use the popup close hook but disable ESC key closing
  const popupRef = usePopupClose(isOpen, onClose, false, false);

  // Convert hiddenAttributes array to Set for efficient lookup
  const hiddenAttributesSet = new Set(hiddenAttributes);

  // Initialize the edited data when the modal opens
  useEffect(() => {
    if (isOpen && data) {
      setEditedData(JSON.parse(JSON.stringify(data))); // Deep copy
    }
  }, [isOpen, data]);

  const handleSave = async () => {
    if (!editedData) {
      alert('No data to save');
      return;
    }

    if (!onSave) {
      alert('No save handler provided');
      return;
    }

    try {
      setIsSaving(true);

      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }

      // Call the provided save function
      await onSave(editedData);

      // Close modal
      onClose();

    } catch (err) {
      console.error('Error saving data:', err);
      alert(`Failed to save: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedData(null);
    onClose();
  };

  const handleDataChange = (data) => {
    setEditedData(data);
  };

  // Filter function to hide specific attributes
  const searchFilter = ({ key, path }) => {
    // Hide attributes that are in the hiddenAttributes set
    const fullPath = path.join('.');
    const keyPath = `${fullPath}.${key}`;
    
    // Check if this specific key or any parent path is hidden
    return !hiddenAttributesSet.has(key) && !hiddenAttributesSet.has(keyPath) && !hiddenAttributesSet.has(fullPath);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]" style={{ margin: 0, padding: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleCancel}></div>
      
      {/* Modal Container - True Fullscreen */}
      <div ref={popupRef} className="relative bg-gray-900 flex flex-col" style={{ width: '100vw', height: '100vh', margin: 0, padding: 0 }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-white">{title}</h2>
            <p className="text-gray-300 mt-1">{description}</p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors ${isSaving ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                saveButtonText
              )}
            </button>
          </div>
        </div>
        
        {/* Content Area - Takes remaining space */}
        <div className="flex-1 overflow-auto p-6">
          <div className="my-4">
            {editedData && (
              <JsonEditor
                data={editedData}
                setData={handleDataChange}
                rootName={rootName}
                searchFilter={hiddenAttributes.length > 0 ? searchFilter : undefined}
                theme={[
                  githubDarkTheme,
                  {
                    styles: {
                      container: {
                        backgroundColor: 'transparent'
                      }
                    }
                  }
                ]}
                collapse={5}
                enableClipboard={false}
                showErrorMessages={true}
                showStringQuotes={false}
                showCollectionCount={false}
                restrictEdit={false}
                restrictDelete={false}
                restrictAdd={false}
                restrictTypeSelection={false}
                minWidth="100%"
                maxWidth="100%"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawJsonEditor;
